import { FC, useMemo } from 'react';
import { Button, Dropdown, MenuProps } from 'antd';
import * as Icon from 'react-feather';

import { ApiErrorResponse, ClassifierFormDataType } from '@atlas-frontend/sdk';

import { ClassifierUpload } from './ClassifierUpload';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './CategoryUploadDownloadActions.translations.json';
import { ClassifierDownload } from './ClassifierDownload';

const { addTranslations, useTranslation } = i18nNsFactory(
  'category-upload-download-actions',
);
addTranslations(translations);

export interface CategoryUploadDownloadProps {
  id?: number;
  edit?: boolean;
  onError?: (error: ApiErrorResponse) => void;
  onSuccess: (data: Partial<ClassifierFormDataType>) => void;
}

export const CategoryUploadDownloadActions: FC<CategoryUploadDownloadProps> = ({
  id,
  edit,
  onError,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const downloadMessage = edit ? t('downloadConfirm') : undefined;
  const uploadMessage = id ? t('replaceConfirm') : undefined;
  const downloadOptions: MenuProps['items'] = useMemo(
    () =>
      id
        ? [
            {
              label: (
                <ClassifierDownload
                  classifierId={id}
                  sections={[
                    'CLASSIFIER',
                    'CATEGORIES',
                    'CATEGORY_GROUPS',
                    'COMBINED_GROUPS',
                  ]}
                  confirmMessage={downloadMessage}
                >
                  <span>{t('download.classifier')}</span>
                </ClassifierDownload>
              ),
              key: '1',
            },
            {
              label: (
                <ClassifierDownload
                  classifierId={id}
                  sections={['CATEGORIES']}
                  confirmMessage={downloadMessage}
                >
                  <span>{t('download.categories')}</span>
                </ClassifierDownload>
              ),
              key: '2',
            },
            {
              label: (
                <ClassifierDownload
                  classifierId={id}
                  sections={['CATEGORY_GROUPS']}
                  confirmMessage={downloadMessage}
                >
                  <span>{t('download.groups')}</span>
                </ClassifierDownload>
              ),
              key: '3',
            },
            {
              label: (
                <ClassifierDownload
                  classifierId={id}
                  sections={['COMBINED_GROUPS']}
                  confirmMessage={downloadMessage}
                >
                  <span>{t('download.combined-groups')}</span>
                </ClassifierDownload>
              ),
              key: '4',
            },
          ]
        : [],
    [downloadMessage, id, t],
  );

  const uploadOptions: MenuProps['items'] = useMemo(
    () =>
      edit
        ? [
            {
              label: (
                <ClassifierUpload
                  classifierId={id}
                  sections={[
                    'CLASSIFIER',
                    'CATEGORIES',
                    'CATEGORY_GROUPS',
                    'COMBINED_GROUPS',
                  ]}
                  onError={onError}
                  onSuccess={onSuccess}
                  confirmMessage={uploadMessage}
                  label={t('upload.classifier')}
                  triggerVariant="label"
                />
              ),
              key: '1',
            },
            {
              label: (
                <ClassifierUpload
                  classifierId={id}
                  sections={['CATEGORIES']}
                  onError={onError}
                  onSuccess={onSuccess}
                  confirmMessage={uploadMessage}
                  label={t('upload.categories')}
                  triggerVariant="label"
                />
              ),
              key: '2',
            },
            {
              label: (
                <ClassifierUpload
                  classifierId={id}
                  sections={['CATEGORY_GROUPS']}
                  onError={onError}
                  onSuccess={onSuccess}
                  confirmMessage={uploadMessage}
                  label={t('upload.groups')}
                  triggerVariant="label"
                />
              ),
              key: '3',
            },
            {
              label: (
                <ClassifierUpload
                  classifierId={id}
                  sections={['COMBINED_GROUPS']}
                  onError={onError}
                  onSuccess={onSuccess}
                  confirmMessage={uploadMessage}
                  label={t('upload.combined-groups')}
                  triggerVariant="label"
                />
              ),
              key: '4',
            },
          ]
        : [],
    [edit, id, onError, onSuccess, uploadMessage, t],
  );

  return (
    <>
      {edit && (
        <Dropdown menu={{ items: uploadOptions }} placement="bottom">
          <Button id="upload-category" icon={<Icon.Upload />} />
        </Dropdown>
      )}
      {!!id && (
        <Dropdown menu={{ items: downloadOptions }} placement="bottom">
          <Button id="download-category" icon={<Icon.Download />} />
        </Dropdown>
      )}
    </>
  );
};
